import { UserModel, UserSearchModel } from "../lib/Domain/User/User";
import BusinessCard from "../components/BusinessCard";
import Goal from "../lib/Domain/Types/Goal";
import { Hit } from "instantsearch.js";
import {
  HitAttributeHighlightResult,
  HitHighlightResult,
} from "instantsearch.js/es/types/results";
import { InterestTitles } from "../types/user/interests";

export const toBusinessCard = (
  user: UserModel | Hit<UserSearchModel>
): BusinessCard => {
  const isHit = (
    user: UserModel | Hit<UserSearchModel>
  ): user is Hit<UserSearchModel> => {
    return (<Hit<UserSearchModel>>user)._highlightResult !== undefined;
  };

  if (isHit(user)) {
    // This is for searching, it generates results that highlight
    const h = user._highlightResult;
    const getValue = (a): string | null => (a && a.value) || null;
    const getValues = (a): (typeof a)[] | null =>
      (a && Array.isArray(a) && a.map((arr) => arr.value)) || null;

    return {
      id: user.id || user.objectID,
      displayName: getValue(h.name) || user.name,
      imageSrc: user.imageSrc,
      businessCardColour: user.businessCardColour,
      location: getValue(h.location) || user.location,
      job: getValue(h.jobTitle) || user.jobTitle,
      jobLevel: getValue(h.jobLevel) || user.jobLevel,
      jobIndustry: getValue(h.jobIndustry) || user.jobIndustry,
      jobCompany: getValue(h.jobCompany) || user.jobCompany,
      displayPhoto: user.displayPhoto !== false,
      goals: getValues(h.goals) || user.goals,
      interests: getValues(h.interests) || user.interests,
      objectID: h.objectID,
      __position: h.__position,
      __queryID: h.__queryID,
    };
  }

  return {
    id: user.id || user.uid,
    displayName: user.name || `${user.firstName} ${user.lastName}`,
    imageSrc: user.imageSrc || user.profileImageUrl,
    businessCardColour: user.businessCardColour,
    location:
      `${[user.city, user.country].filter(Boolean).join(", ")}` ||
      user.location,
    job: user.jobTitle,
    jobLevel: user.jobLevel,
    jobIndustry: user.jobIndustry,
    jobCompany: user.jobCompany,
    displayPhoto: user.displayPhoto !== false,
    goals: user.goals,
    interests: user.interests,
  };
};

type BusinessCard = {
  displayName: string;
  businessCardColour: string;
  location?: string;
  job?: string;
  jobLevel?: string;
  jobIndustry?: string;
  jobCompany?: string;
  displayPhoto: boolean;
  id?: string;
  imageSrc?: string;
  goals?: (typeof Goal)[number][];
  interests?: InterestTitles;
  objectID?: HighlightDataField;
  __position?: HighlightDataField;
  __queryID?: HighlightDataField;
};

type HighlightDataField =
  | HitAttributeHighlightResult
  | HitAttributeHighlightResult[]
  | HitHighlightResult[]
  | HitHighlightResult;
